(ns manage.slate-editor.query-param-node
  (:require ["@mui/material/Box$default" :as Box]
            ["slate" :as slate]
            [clojure.string :as str]
            [common.util :refer [spread-props]]
            [manage.slate-editor.common :as common]
            [manage.slate-editor.util :as util :refer [with-ref]]
            [uix.core :refer [defui $]]))

(defn- is-valid-query-attribute-node? [^js node]
  (and (= (.-type node) common/query-param-attribute-node-type)
       (let [text (slate/Node.string node)]
         (some? (re-matches common/query-param-attribute-node-regex text)))))

(defn- is-valid-query-value-node? [^js node]
  (and (= (.-type node) common/query-param-value-node-type)
       (let [text (slate/Node.string node)]
         (some? (re-matches common/query-param-value-node-regex text)))))

(defn- is-valid-query-node? [^js node]
  (let [type (.-type node)
        children (->> (.-children node)
                      (remove (fn [child]
                                (= (.-text child) ""))))]
    (and (= type common/query-param-node-type)
         (= (count children) 3)
         (let [[left-child center-child right-child] children]
           (and (is-valid-query-attribute-node? left-child)
                (= (.-text center-child) "=")
                (is-valid-query-value-node? right-child))))))

(defn- unwrap-invalid-query-param-nodes [^js editor ^js node-entry original-normalize-node]
  (let [[node path] node-entry]
    (if (and (= (.-type node) common/query-param-node-type)
             (not (is-valid-query-node? node)))
      (util/flatten-node editor path)
      (original-normalize-node node-entry))))

(defn wrap-query-param-node
  [^js editor ^js path query-param-index attribute-text value-text]
  (with-ref [^js attribute-start-ref (let [[child-path text-index] (util/deeper-path+text-index editor
                                                                                                path
                                                                                                query-param-index
                                                                                                :forward)]
                                       (slate/Editor.pointRef editor
                                                              #js {:path child-path
                                                                   :offset text-index}
                                                              #js {:affinity "forward"}))
             ^js attribute-end-ref (let [[child-path text-index] (util/deeper-path+text-index editor
                                                                                              path
                                                                                              (+ query-param-index
                                                                                                 (count attribute-text))
                                                                                              :backward)]
                                     (slate/Editor.pointRef editor
                                                            #js {:path child-path
                                                                 :offset text-index}
                                                            #js {:affinity "backward"}))
             ^js value-start-ref (let [[child-path text-index] (util/deeper-path+text-index editor
                                                                                            path
                                                                                            (+ query-param-index
                                                                                               (count attribute-text)
                                                                                               (count "="))
                                                                                            :forward)]
                                   (slate/Editor.pointRef editor
                                                          #js {:path child-path
                                                               :offset text-index}
                                                          #js {:affinity "forward"}))
             ^js value-end-ref (let [[child-path text-index] (util/deeper-path+text-index editor
                                                                                          path
                                                                                          (+ query-param-index
                                                                                             (count attribute-text)
                                                                                             (count "=")
                                                                                             (count value-text))
                                                                                          :backward)]
                                 (slate/Editor.pointRef editor
                                                        #js {:path child-path
                                                             :offset text-index}
                                                        #js {:affinity "backward"}))]
    (slate/Transforms.wrapNodes editor
                                #js {:type common/query-param-node-type}
                                #js {:at   #js {:anchor (.-current attribute-start-ref)
                                                :focus (.-current value-end-ref)}
                                     :mode "highest"
                                     :split true})
    (let [[query-param-node-path _text-index] (util/deeper-path+text-index editor
                                                                           path
                                                                           query-param-index
                                                                           :forward)]
      (slate/Transforms.wrapNodes editor
                                  #js {:type common/query-param-attribute-node-type}
                                  #js {:at   #js {:anchor (.-current attribute-start-ref)
                                                  :focus (.-current attribute-end-ref)}
                                       :match (fn [^js _match-node ^js match-path]
                                                (slate/Path.isParent query-param-node-path match-path))
                                       :mode "highest"
                                       :split true})
      (slate/Transforms.wrapNodes editor
                                  #js {:type common/query-param-value-node-type}
                                  #js {:at   #js {:anchor (.-current value-start-ref)
                                                  :focus (.-current value-end-ref)}
                                       :match (fn [^js _match-node ^js match-path]
                                                (slate/Path.isParent query-param-node-path match-path))
                                       :mode "highest"
                                       :split true}))))

(defn- wrap-query-param-nodes [^js editor ^js node-entry original-normalize-node]
  (let [transforms-applied (atom 0)
        [node path] node-entry]
    (when (= (.-type node) "paragraph")
      (let [raw-text (slate/Node.string node)
            ;; The matches based on regex parsing of the raw text in the paragraph.
            index+size->match (->> raw-text
                                   (util/re-partition-indexed (common/query-param-node-regex))
                                   (keep (fn [{:keys [index size match]}]
                                           (when (some? match)
                                             [[index size] match])))
                                   (into {}))
            ;; Find the query-param nodes which should be unwrapped:
            ;; - Retrieve the query-param nodes in this paragraph.
            ;; - For each of them, find where they start and end in this paragraph's raw text.
            query-param-nodes-info (->> (util/children-nodes-info editor path)
                                        (filterv (fn [{:keys [node]}]
                                                   (= (.-type node) common/query-param-node-type))))]
        ;; - Those which are not in index+size->match should be unwrapped.
        (doseq [{:keys [index size path]} (rseq query-param-nodes-info)
                :when (not (contains? index+size->match [index size]))]
          (util/flatten-node editor path)
          (swap! transforms-applied inc))

        ;; Find the raw-text matches which do not exist in the children, then wrap them.
        (let [children-query-param-index+size (into #{}
                                                    (map (fn [{:keys [index size]}]
                                                           [index size]))
                                                    query-param-nodes-info)]
          (doseq [[[index size] [_ [_ attribute-text] [_ value-text]]] index+size->match
                  :when (not (contains? children-query-param-index+size [index size]))]
            (wrap-query-param-node editor path index attribute-text value-text)
            (swap! transforms-applied inc)))))

    (when (zero? @transforms-applied)
      (original-normalize-node node-entry))))

(defn- decode-uri-components [^js editor ^js data-transfer original-insert-text-data]
  ;; More information at https://github.com/ianstormtaylor/slate/blob/e18879e728077b09580b29e9a6683aaa66629bc5/packages/slate-react/src/plugin/with-react.ts#L301-L318
  (if-some [text (.getData data-transfer "text/plain")]
    (let [text (-> text
                   (str/replace #"[\n\r]" "")
                   (js/decodeURIComponent))]
      (.insertText editor text)
      true)
    (original-insert-text-data data-transfer)))

(defn with-query-param-node-support [^js editor]
  (-> editor
      (util/with-inline-element-types [common/query-param-node-type
                                       common/query-param-attribute-node-type
                                       common/query-param-value-node-type])

      ;; This runs in second (after the first)
      (util/with-normalize-node wrap-query-param-nodes)
      ;; This runs first
      (util/with-normalize-node unwrap-invalid-query-param-nodes)
      #_(util/with-normalize-node debug/log-normalize-node-calls)

      (util/with-insert-text-data decode-uri-components)))

(defui query-param-attribute-component [{:keys [attributes children]}]
  ($ :span attributes children))

(defui query-param-value-component [{:keys [attributes children]}]
  ($ :span attributes children))

(defui query-param-component [{:keys [attributes children]}]
  ($ Box (spread-props {:component "span"
                        :sx #js {:p 0.5
                                 :fontWeight 600
                                 :borderRadius 1.5
                                 :bgcolor "grey.60"
                                 :color "grey.10"}}
                       attributes)
     children))
